import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { useCart } from "@/contexts/CartContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { LogoFull } from "@/components/Logo";
import { Menu, X, ShoppingBag } from "lucide-react";

export default function Header() {
  const { t } = useI18n();
  const { count, openDrawer } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = location.pathname === "/";

  const navLinks = [
    { id: "shop", label: t.nav.shop },
    { id: "story", label: t.nav.story },
    { id: "newsletter", label: t.nav.newsletter },
  ];

  const goToSection = (id) => {
    setMobileOpen(false);
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? "bg-black/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-2 group transition-transform hover:scale-[1.03]"
        >
          <LogoFull height={36} color="#FFFFFF" accent="#D4AF37" jewel="#A93043" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => goToSection(link.id)}
              data-testid={`nav-link-${link.id}`}
              className="kh-link font-body text-sm uppercase tracking-[0.2em] font-medium"
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />

          {/* Cart icon */}
          <button
            type="button"
            onClick={openDrawer}
            data-testid="cart-open-btn"
            aria-label={t.cart.title}
            className="relative w-10 h-10 flex items-center justify-center border border-[#262626] text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#D4AF37] text-black font-display text-[10px] tracking-tight"
              >
                {count}
              </span>
            )}
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="md:hidden w-10 h-10 flex items-center justify-center border border-[#262626] hover:border-[#D4AF37] transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
        >
          <nav className="flex flex-col px-6 py-6 gap-5">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => goToSection(link.id)}
                data-testid={`mobile-nav-link-${link.id}`}
                className="text-left font-headings text-2xl tracking-[0.1em] text-white hover:text-[#D4AF37] transition-colors"
              >
                {link.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
